import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertTriangle, FileSpreadsheet, XCircle } from 'lucide-react';
import { appMonthNameYear } from '@/utils/appDateFormat';
import { Result } from '../../../models/general';
import { PageLayout } from '../../../components/ui/PageLayout';
import Button from '../../../components/ui/Button';
import YearMonthPicker from '../../../components/yearMonthPicker';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import {
  getSifPreflight,
  getWpsCoverage,
  getWpsDeadline,
  getWpsDueMonth,
  getWpsHistory,
  getWpsSettings,
} from '../../../api/Payroll/wps';
import {
  SifPreflightView,
  WpsCoverageView,
  WpsDeadlineView,
  WpsDueMonthView,
  WpsEmployeeClass,
  WpsSettingsView,
  WpsSifSubmissionView,
} from '../../../models/payroll';
import { MohreTimeline } from '../ComplianceCenter/components/MohreTimeline';
import { ChecklistButton } from './components/ChecklistButton';
import { CompanySetupCard } from './components/CompanySetupCard';
import { FileHistory } from './components/FileHistory';
import { GeneratePanel } from './components/GeneratePanel';
import { HoldAnotherEmployee, IssuesPanel } from './components/IssuesPanel';
import { ClockOffNote, MonthStatusLine } from './components/MonthStatusLine';
import { OverdueLines } from './components/OverdueLines';
import { PeoplePanel } from './components/PeoplePanel';
import { PreflightChecklistDialog } from './components/PreflightChecklistDialog';
import { defaultWpsMonth, useWpsLabels, wpsClockRunning } from './components/wpsLabels';

type LoadPart = 'preflight' | 'deadline' | 'coverage' | 'history' | 'due';

interface MonthData {
  preflight: SifPreflightView | null;
  deadline: WpsDeadlineView | null;
  coverage: WpsCoverageView | null;
  /** What did not answer. A missing answer must never read as nothing owed. */
  failed: LoadPart[];
}

const EMPTY_MONTH: MonthData = { preflight: null, deadline: null, coverage: null, failed: [] };

const didFail = <T,>(answer: PromiseSettledResult<Result<T>>): boolean =>
  answer.status === 'rejected' || answer.value.errors.length > 0;

/**
 * The UAE WPS Center, on the per-employee file record and the MR 340/2026 clock. It coordinates and loads; each block
 * of the screen is its own component. Top to bottom: what else is open, where this month stands, what stops the file,
 * the file itself, who is where, the legal steps, and the files made so far.
 */
export const UaeWpsCenter: React.FC = () => {
  const { t, i18n } = useTranslation();
  const labels = useWpsLabels();
  const { toast, showToast, dismissToast } = useToast();

  // Null until the due month answers, because the page opens on the most recently due month while it is open.
  const [period, setPeriod] = useState<{ year: number; month: number } | null>(null);
  const [dueMonth, setDueMonth] = useState<WpsDueMonthView | null>(null);
  const [settings, setSettings] = useState<WpsSettingsView | null>(null);
  const [history, setHistory] = useState<WpsSifSubmissionView[]>([]);
  const [data, setData] = useState<MonthData>(EMPTY_MONTH);
  const [loading, setLoading] = useState(true);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [peopleGroup, setPeopleGroup] = useState<WpsEmployeeClass | null>(null);
  const requestSeq = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const today = new Date();
    void (async () => {
      const [due, currentSettings] = await Promise.allSettled([getWpsDueMonth(), getWpsSettings()]);
      if (cancelled) return;
      const duePayload = due.status === 'fulfilled' && due.value.errors.length === 0 ? due.value.payload : null;
      setDueMonth(duePayload);
      if (currentSettings.status === 'fulfilled' && currentSettings.value.errors.length === 0) {
        setSettings(currentSettings.value.payload);
      }
      setPeriod(defaultWpsMonth(duePayload, today));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    if (!period) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    const { year, month } = period;
    const [preflight, deadline, coverage, files, due] = await Promise.allSettled([
      getSifPreflight(year, month),
      getWpsDeadline(year, month),
      getWpsCoverage(year, month),
      getWpsHistory(),
      getWpsDueMonth(),
    ]);
    // A newer month was picked while this batch was in flight, so these numbers belong under a heading no longer shown.
    if (seq !== requestSeq.current) return;
    const failed: LoadPart[] = [];
    if (didFail(preflight)) failed.push('preflight');
    if (didFail(deadline)) failed.push('deadline');
    if (didFail(coverage)) failed.push('coverage');
    if (didFail(files)) failed.push('history');
    if (didFail(due)) failed.push('due');
    setData({
      preflight: preflight.status === 'fulfilled' && preflight.value.errors.length === 0 ? preflight.value.payload : null,
      deadline: deadline.status === 'fulfilled' && deadline.value.errors.length === 0 ? deadline.value.payload : null,
      coverage: coverage.status === 'fulfilled' && coverage.value.errors.length === 0 ? coverage.value.payload : null,
      failed,
    });
    if (files.status === 'fulfilled' && files.value.errors.length === 0) setHistory(files.value.payload ?? []);
    if (due.status === 'fulfilled' && due.value.errors.length === 0) setDueMonth(due.value.payload);
    setLoading(false);
  }, [period]);

  useEffect(() => {
    setData(EMPTY_MONTH);
    void load();
  }, [load]);

  const { preflight, deadline, coverage, failed } = data;
  const year = period?.year ?? new Date().getFullYear();
  const month = period?.month ?? new Date().getMonth() + 1;
  const monthName = appMonthNameYear(new Date(year, month - 1));

  const holdBackEnabled = settings?.holdBackEnabled ?? true;
  const issues = useMemo(() => preflight?.employeeIssues ?? [], [preflight]);
  const blockedPeople = useMemo(
    () => new Set(issues.filter(i => !i.isWarning).map(i => i.userId)).size,
    [issues],
  );
  const warnedPeople = useMemo(
    () => new Set(issues.filter(i => i.isWarning).map(i => i.userId)).size,
    [issues],
  );
  const companyFailCount = preflight?.companyChecks?.filter(c => !c.passed).length ?? 0;
  const legacy = preflight?.hasLegacyFile === true;
  const hasNames = preflight !== null && preflight.employeesTotal > 0;

  const generated = useCallback(() => {
    showToast(t('payroll.wps.uae.generate.done'), 'success');
    void load();
  }, [load, showToast, t]);

  const notify = useCallback((message: string) => showToast(message, 'success'), [showToast]);
  const changed = useCallback(() => void load(), [load]);
  const noPayrollWarnOnly = settings?.noPayrollRowMode === 'WarnOnly';

  const showHeldPeople = useCallback(() => {
    setPeopleGroup('Held');
    document.getElementById('wps-people')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const failedLines = (() => {
    if (failed.includes('preflight') && failed.includes('deadline') && failed.includes('coverage')) {
      return [t('payroll.wps.uae.loadFailed.all')];
    }
    return failed.map(part => {
      switch (part) {
        case 'preflight': return t('payroll.wps.uae.loadFailed.preflight');
        case 'deadline': return t('payroll.wps.uae.loadFailed.deadline');
        case 'coverage': return t('payroll.wps.uae.loadFailed.coverage');
        case 'history': return t('payroll.wps.uae.loadFailed.history');
        default: return t('payroll.wps.uae.loadFailed.due');
      }
    });
  })();

  return (
    <PageLayout
      title={t('payroll.wps.titleUae', 'WPS')}
      icon={FileSpreadsheet}
      accentColor="emerald"
      headerActions={
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm" iconName="ArrowLeft">
            <Link to="/payroll/table">{t('Back to Payroll Table')}</Link>
          </Button>
          <Button asChild variant="outline" size="sm" iconName="ExternalLink">
            <Link to="/payroll/compliance-center">{t('Compliance Center')}</Link>
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4" data-testid="wps-center">
        <OverdueLines
          dueMonth={dueMonth}
          year={year}
          month={month}
          onOpen={(y, m) => setPeriod({ year: y, month: m })}
        />

        <div className="flex flex-wrap items-center gap-2">
          <YearMonthPicker
            date={new Date(year, month - 1)}
            onChange={(d: Date) => setPeriod({ year: d.getFullYear(), month: d.getMonth() + 1 })}
            isRtl={i18n.dir() === 'rtl'}
          />
          <Button variant="outline" size="sm" iconName="RefreshCw" onClick={changed} loading={loading}>
            {t('payroll.complianceCenter.wps.recheck', 'Re-run checks')}
          </Button>
          {preflight && <ChecklistButton onClick={() => setChecklistOpen(true)} />}
        </div>

        {failedLines.length > 0 && !loading && (
          <div
            className="rounded-lg border border-red-200 bg-red-50 p-3 flex flex-wrap items-center justify-between gap-3 dark:border-red-800 dark:bg-red-950/30"
            role="alert"
            data-testid="wps-load-failed"
          >
            <div className="flex items-start gap-2 text-sm font-medium text-red-800 dark:text-red-200">
              <XCircle className="w-4 h-4 mt-0.5 shrink-0 text-red-500" aria-hidden="true" />
              <div className="space-y-0.5">
                {failedLines.map(line => <p key={line}>{line}</p>)}
              </div>
            </div>
            <Button variant="outline" size="sm" iconName="RefreshCw" onClick={changed} data-testid="wps-load-failed-retry">
              {t('payroll.complianceCenter.wps.recheck', 'Re-run checks')}
            </Button>
          </div>
        )}

        {(deadline?.clockOff || settings?.filedOutsideMowazaf) && <ClockOffNote />}

        {deadline && <MonthStatusLine year={year} month={month} deadline={deadline} coverage={coverage} />}

        {legacy && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200" data-testid="wps-legacy-banner">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" aria-hidden="true" />
            <span>{t('payroll.wps.uae.banner.legacy')}</span>
          </div>
        )}

        {hasNames && !legacy && (blockedPeople > 0 || companyFailCount > 0) && (
          <div
            className="rounded-lg border border-red-200 bg-red-50 p-3 flex flex-wrap items-center justify-between gap-3 dark:border-red-800 dark:bg-red-950/30"
            data-testid="wps-blocking-banner"
          >
            <p className="flex items-start gap-2 text-sm font-medium text-red-800 dark:text-red-200">
              <XCircle className="w-4 h-4 mt-0.5 shrink-0 text-red-500" aria-hidden="true" />
              <span>
                {blockedPeople > 0
                  ? t('payroll.wps.uae.banner.blocked', { count: blockedPeople })
                  : t('payroll.wps.uae.banner.company', { n: companyFailCount })}
              </span>
            </p>
            {blockedPeople > 0 && (
              <Button
                variant="outline"
                size="sm"
                iconName="ArrowDown"
                onClick={() => document.getElementById('employee-issues')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              >
                {t('payroll.wps.reviewIssues', 'Review issues')}
              </Button>
            )}
          </div>
        )}
        {hasNames && !legacy && blockedPeople === 0 && companyFailCount === 0 && warnedPeople > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200" data-testid="wps-warning-banner">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" aria-hidden="true" />
            <span>{t('payroll.wps.uae.banner.warnings', { n: warnedPeople })}</span>
          </div>
        )}

        <GeneratePanel
          year={year}
          month={month}
          monthName={monthName}
          preflight={preflight}
          coverage={coverage}
          holdBackEnabled={holdBackEnabled}
          loading={loading}
          noPayrollWarnOnly={noPayrollWarnOnly}
          onShowHeld={showHeldPeople}
          onChanged={changed}
          onGenerated={generated}
        />

        {preflight && !preflight.companyPassed && <CompanySetupCard preflight={preflight} />}

        <IssuesPanel year={year} month={month} issues={issues} holdBackEnabled={holdBackEnabled} onChanged={changed} />

        {holdBackEnabled && !legacy && (
          <HoldAnotherEmployee year={year} month={month} people={coverage?.items ?? []} onChanged={changed} />
        )}

        <PeoplePanel
          year={year}
          month={month}
          coverage={coverage}
          holdBackEnabled={holdBackEnabled}
          noPayrollWarnOnly={noPayrollWarnOnly}
          expanded={peopleGroup}
          onExpandedChange={setPeopleGroup}
          onChanged={changed}
        />

        <MohreTimeline
          daysAfterDue={deadline && wpsClockRunning(deadline.zone) ? deadline.daysAfterDue ?? null : null}
          stopped={deadline?.monthStatus === 'Submitted'}
        />

        {preflight?.companyPassed && <CompanySetupCard preflight={preflight} />}

        <FileHistory files={history} loading={loading} loadFailed={failed.includes('history')} onChanged={changed} onNotify={notify} />

        <PreflightChecklistDialog
          open={checklistOpen}
          onOpenChange={setChecklistOpen}
          preflight={preflight}
          issueLabel={labels.issue}
        />

        <ToastRenderer toast={toast} onDismiss={dismissToast} />
      </div>
    </PageLayout>
  );
};
